<script setup lang="ts">
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import { isBulkStorage, isCoilStorage, isLogYard, isPackageStorage, isPackagePlaceArea, isProductionLine } from 'shared/storageAreas/domain/storageAreaType';
import { useSelectedStorageAreaStore } from '../stores/selectedStorageAreaStore';
import { storeToRefs } from 'pinia';
import { InputText, Select, Message, ToggleSwitch, Button, InputGroup } from 'primevue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import LogYardPanel from './LogYardPanel.vue';
import CoilStackPanel from './CoilStackPanel.vue';
import Textarea from 'primevue/textarea';
import { useAuthStore } from '@/auth/store/authStore';
import BulkStoragePanel from './BulkStoragePanel.vue';
import PackageStoragePanel from './PackageStoragePanel.vue';
import ProductionLinePanel from './ProductionLinePanel.vue';
import DisplayTextFormulaDialog from './DisplayTextFormulaDialog.vue';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import { computeStorageAreaDisplayText } from 'shared/storageAreas/domain/computeDisplayText';
import { computed, ref } from 'vue';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore.ts';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const authStore = useAuthStore();
const settingStore = useSettingStore();
const featureFlags = useFeatureFlagStore();

const statusList = [
  StorageAreaStatus.Undefined,
  StorageAreaStatus.Empty,
  StorageAreaStatus.InUse,
  StorageAreaStatus.Full,
];

const disabledStatus = [
  StorageAreaStatus.Undefined,
  StorageAreaStatus.Empty,
]


const statusOptions = statusList.map<StatusOption>((t) => {
  return {
    value: t,
    name: translations.value.enums.storageAreaStatus(t),
    disabled: disabledStatus.some(status => status == t)
  };
});

const {
  name,
  nameChanged,
  nameError,
  nameEnabled,
  type,
  typeChanged,
  typeEnabled,
  status,
  statusChanged,
  statusEnabled,
  typeOptions,
  note,
  noteChanged,
  noteEnabled,
  active,
  activeChanged,
  activeEnabled,
  importantNote,
  importantNoteChanged,
  importantNoteEnabled,
  editMultiple,
  currentStorageArea
} = storeToRefs(selectedStorageAreaStore);

const showDisplayTextFormulaDialog = ref(false);

const displayTextPreview = computed(() => {
  const area = currentStorageArea.value;
  if (!area) return '';

  return computeStorageAreaDisplayText(
    settingStore.setting.sharedClient.storageArea.displayTextFormula,
    {
      ...area,
      name: name.value ?? area.name,
      type: type.value ?? area.type,
      status: status.value ?? area.status,
      note: note.value ?? area.note,
      importantNote: importantNote.value ?? area.importantNote,
      active: active.value ?? area.active,
    },
  );
});

interface StatusOption {
  name: string;
  value: StorageAreaStatus;
  disabled: boolean;
}

</script>

<template>
  <form class="storage-area-form">

    <div class="field" :class="{ changed: nameChanged }">
      <label for="storage-area-drawer-name">{{ translations.generic.name }}</label>
      <InputText autocomplete="off" :invalid="nameError !== undefined" v-model:modelValue="name" autofocus
        id="storage-area-drawer-name" name="name" :disabled="!authStore.isAdmin || !nameEnabled"></InputText>
      <Message v-if="nameError" severity="error" variant="simple">{{ nameError }}</Message>
    </div>

    <div class="field" :class="{ changed: typeChanged }">
      <label for="storage-area-drawer-type">{{ translations.generic.type }}</label>
      <Select autocomplete="off" id="storage-area-drawer-type" :disabled="!authStore.isAdmin || !typeEnabled" name="type"
        :options="typeOptions" option-label="name" option-value="value" v-model="type">
      </Select>
    </div>

    <div class="field" :class="{ changed: statusChanged }">
      <label for="storage-area-drawer-status">{{ translations.generic.status }}</label>
      <Select autocomplete="off" id="storage-area-drawer-status" name="status" :options="statusOptions"
        option-disabled="disabled" option-label="name" option-value="value" v-model="status" :disabled="!statusEnabled">
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex items-center">
            <div>{{ translations.enums.storageAreaStatus(slotProps.value) }}</div>
          </div>
          <span v-else>
            {{ slotProps.placeholder }}
          </span>
        </template>
      </Select>
    </div>

    <div class="field" :class="{ changed: noteChanged }">
      <label for="storage-area-drawer-note">{{ translations.generic.note }}</label>
      <Textarea id="storage-area-drawer-note" v-model="note" :disabled="!noteEnabled"></Textarea>
    </div>

    <div class="field">
      <label for="storage-area-drawer-display-text">{{
        translations.systemSettings.page.storagearea.displayText }}</label>
      <InputGroup>
        <InputText id="storage-area-drawer-display-text" :modelValue="displayTextPreview" readonly
          :disabled="editMultiple"></InputText>
        <Button v-if="authStore.isAdmin" icon="pi pi-cog" severity="secondary"
          :title="translations.systemSettings.page.storagearea.configureDisplayText"
          @click="showDisplayTextFormulaDialog = true"></Button>
      </InputGroup>
    </div>

    <div class="toggle-field" :class="{ changed: importantNoteChanged }">
      <ToggleSwitch v-model:modelValue="importantNote" id="storage-area-drawer-important-note" :disabled="!authStore.isAdmin || !importantNoteEnabled">
      </ToggleSwitch>
      <label for="storage-area-drawer-important-note">{{
        translations.generic.important }}</label>
    </div>

    <div class="toggle-field" :class="{ changed: activeChanged }">
      <ToggleSwitch v-model:modelValue="active" id="storage-area-drawer-active" :disabled="!authStore.isAdmin || !activeEnabled">
      </ToggleSwitch>
      <label for="storage-area-drawer-active">{{
        translations.generic.active }}</label>
    </div>

    <LogYardPanel v-if="featureFlags.isLogYardEnabled() && isLogYard(type) && (!editMultiple || typeEnabled)">
    </LogYardPanel>

    <CoilStackPanel v-if="featureFlags.isCoilEnabled() && isCoilStorage(type) && (!editMultiple || typeEnabled)">
    </CoilStackPanel>

    <BulkStoragePanel v-if="featureFlags.isBulkEnabled() && isBulkStorage(type) && (!editMultiple || typeEnabled)">
    </BulkStoragePanel>

    <PackageStoragePanel v-if="featureFlags.isPackagesEnabled() && (isPackageStorage(type) || isPackagePlaceArea(type)) && (!editMultiple || typeEnabled)">
    </PackageStoragePanel>

    <ProductionLinePanel v-if="featureFlags.isPackagesEnabled() && isProductionLine(type) && (!editMultiple || typeEnabled)">
    </ProductionLinePanel>

    <DisplayTextFormulaDialog v-model:visible="showDisplayTextFormulaDialog"></DisplayTextFormulaDialog>

  </form>
</template>

<style scoped>
.storage-area-form {
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: 1fr;
}
</style>
